import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";

import { fetchUserProfile } from "../../../../redux/user/profile/profileActions";
import { fetchWatchlistThunk } from "../../../../redux/user/watchlist/watchlistActions";
import { fetchWatchlistSimpleChartsThunk } from "../../../../redux/stockData/nasdaq/stockDataActions";
import { fetchBatchSnapshots } from "../../../../redux/stockData/marketSnapshot/marketSnapshotActions";
import { fetchMarketOverview } from "../../../../redux/stockData/marketSnapshot/marketSnapshotActions";
import { fetchBatchSentimentData } from "../../../../redux/stockSentiment/sentimentActions";
import { fetchStockSentimentAggsBatchThunk } from "../../../../redux/sentimentAggregator/sentimentAggActionThunks";
import { fetchPostsWithFilters } from "../../../../redux/posts/postActions";
import { fetchCredibilityThunk } from "../../../../redux/credibility/credibilityActionThunks";
import { fetchGroups, searchGroupsByStock } from "../../../../redux/userGroups/groups/userGroupies";
import { fetchFollowersThunk } from "../../../../redux/user/following/userFollowActionThunks";
import {
  fetchContextRadarForWatchlist,
  fetchGlobalContextRadar,
  fetchContextRadarForTicker,
} from "../../../../redux/contextRadar/contextRadarActions";
import { fetchStockerSignal } from "../../../../redux/stockerSignal/stockerSignalActions";
import { fetchEdgarFilingsData } from "../../../../redux/stockData/edgar/edgarActions";
import { setCurrentSymbol } from "../../../../redux/stockData/nasdaq/stockDataReducer";
import { fetchStockDataThunk } from "../../../../redux/stockData/nasdaq/stockDataActions";

/**
 * All data-fetching orchestration for the dashboard, extracted from
 * UnifiedStockerDashboard.jsx's 7 effects (see Technical_Docs/Architecture_Rebuild/
 * 49_FULL_STACK_REBUILD_IMPLEMENTATION_PLAN.md, Phase 2). Every effect body
 * and every inline comment explaining a prior bug fix is preserved exactly.
 * One small, deliberate correctness fix made during the move, disclosed
 * rather than silently folded in: `featureFlags.posts` is now in Effect 3's
 * and Effect 5's dependency arrays, since both effects already read it --
 * the original omitted it (an exhaustive-deps gap, not an intentional
 * choice recorded anywhere). Low practical impact (feature flags rarely
 * change mid-session) but leaving it out was never a deliberate decision,
 * so it's fixed here rather than carried forward uncorrected.
 */
export function useDashboardData({
  user,
  watchlistItems,
  userId,
  featureFlags,
  trendingTab,
  expandedStock,
  currentTimeFrame,
  currentIndicators,
  filings,
}) {
  const dispatch = useDispatch();

  // EFFECT 1 — profile, guarded against the 7x-simultaneous-fetch bug
  // (GlobalBar's dropDown.jsx and profile.jsx also fetch this independently;
  // see 13_FIX_HISTORY.md's Frontend Completeness audit pass).
  useEffect(() => {
    if (!user) dispatch(fetchUserProfile());
  }, [dispatch, user]);

  // EFFECT 1b — watchlist + global context + market overview, fires once per
  // mount only (depends on [dispatch] alone -- see the 2026-08-12 fix note
  // this replaces: this used to share a dependency array with the profile
  // guard above, which re-ran the whole effect body every time `user`'s
  // object reference changed, taking market-overview from 2x to 6x per load).
  useEffect(() => {
    dispatch(fetchWatchlistThunk());
    dispatch(fetchGlobalContextRadar());
    dispatch(fetchMarketOverview());
  }, [dispatch]);

  const retryDashboardData = useCallback(() => {
    dispatch(fetchWatchlistThunk());
    dispatch(fetchGlobalContextRadar());
    dispatch(fetchMarketOverview());
  }, [dispatch]);

  // EFFECT 2 — watchlist-dependent batch data. Single sparkline fetch,
  // single sentiment batch, single context-radar batch -- collapsed from 3
  // separate racing effects in the original (see file history).
  useEffect(() => {
    if (!watchlistItems?.length) return;

    const symbols = watchlistItems.map((w) => w.symbol || w.ticker).filter(Boolean);
    if (!symbols.length) return;

    dispatch(fetchBatchSnapshots(symbols));
    dispatch(fetchBatchSentimentData(symbols, 20));
    dispatch(fetchGroups({ skip: 0, limit: 10 }));
    dispatch(fetchContextRadarForWatchlist({ tickers: symbols, limitPerTicker: 5 }));
    dispatch(fetchWatchlistSimpleChartsThunk({ symbols, days: 90 }));
    dispatch(fetchStockSentimentAggsBatchThunk({ tickers: symbols, timeframe: "24h" }));

    if (userId) {
      dispatch(fetchCredibilityThunk({ userId, soft: true }));
      dispatch(fetchFollowersThunk({ userId }));
      if (symbols[0]) {
        dispatch(searchGroupsByStock({ ticker: symbols[0], limit: 4 }));
      }
    }
  }, [dispatch, watchlistItems, userId]);

  // EFFECT 3 — posts (re-runs when trendingTab changes)
  useEffect(() => {
    if (!watchlistItems?.length || !featureFlags.posts) return;

    dispatch(
      fetchPostsWithFilters({
        skip: 0,
        limit: 20,
        sort_by: trendingTab === "credible" ? "trending" : "latest",
        period: "week",
      })
    );
  }, [dispatch, watchlistItems, trendingTab, featureFlags.posts]);

  // EFFECT 4 — expanded stock: chart + context + signal + filings
  useEffect(() => {
    if (!expandedStock) return;

    dispatch(setCurrentSymbol(expandedStock));
    dispatch(
      fetchStockDataThunk({
        symbol: expandedStock,
        timeFrame: currentTimeFrame,
        indicators: currentIndicators,
      })
    );
    dispatch(fetchContextRadarForTicker(expandedStock));
    dispatch(fetchStockerSignal({ ticker: expandedStock, window: "30d" }));

    if (!filings?.length || filings[0]?.ticker !== expandedStock) {
      dispatch(fetchEdgarFilingsData(expandedStock));
    }
    // intentionally omitting currentTimeFrame / currentIndicators here --
    // those are handled via the manual-fetch handlers in the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, expandedStock]);

  // EFFECT 5 — auto-refresh watchlist data every 5 minutes. Sparkline data
  // deliberately excluded (90-day historical, doesn't meaningfully change
  // within a 5-minute window).
  useEffect(() => {
    if (!watchlistItems?.length) return;

    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;

      const symbols = watchlistItems.map((w) => w.symbol || w.ticker).filter(Boolean);
      if (!symbols.length) return;

      dispatch(fetchBatchSnapshots(symbols));
      dispatch(fetchBatchSentimentData(symbols, 20));
      if (featureFlags.posts) {
        dispatch(
          fetchPostsWithFilters({
            skip: 0,
            limit: 20,
            sort_by: trendingTab === "credible" ? "trending" : "latest",
            period: "week",
          })
        );
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch, watchlistItems, trendingTab, featureFlags.posts]);

  // EFFECT 6 — auto-refresh global context every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      dispatch(fetchGlobalContextRadar());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // EFFECT 7 — auto-refresh expanded stock every 15 minutes
  useEffect(() => {
    if (!expandedStock) return;

    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;

      dispatch(
        fetchStockDataThunk({
          symbol: expandedStock,
          timeFrame: currentTimeFrame,
          indicators: currentIndicators,
        })
      );
      dispatch(fetchContextRadarForTicker(expandedStock));
      dispatch(fetchStockerSignal({ ticker: expandedStock, window: "30d" }));
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch, expandedStock, currentTimeFrame, currentIndicators]);

  return { retryDashboardData };
}
